import { SingleSelectOption } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { ReservedUserGroupName } from '@/modules/common/types/reserved-user-group-name';
import { UserGroup } from '@/modules/common/types/user-group';

interface BuildUserGroupSelectionParams {
  groups: UserGroup[] | undefined;
  /** Builds the label for the default group, e.g. `name => "Member (Default)"`. Keeps i18n in the component. */
  formatDefaultLabel: (name: string) => string;
}

interface UserGroupSelection {
  options: SingleSelectOption[];
  /** Value to preselect, or `undefined` when no selectable group is flagged as default. */
  defaultGroupValue: string | undefined;
}

/**
 * Builds the selectable group options and the value that should be preselected.
 *
 * Both are returned from one call on purpose: `defaultGroupValue` is derived from the already
 * filtered list, so the preselected value is guaranteed to exist in `options` and a Super Admin
 * group can never be preselected even if the API flags it as default.
 */
export const buildUserGroupSelection = ({
  groups,
  formatDefaultLabel,
}: BuildUserGroupSelectionParams): UserGroupSelection => {
  const selectableGroups = (groups ?? []).filter(
    group => group.name !== ReservedUserGroupName.SUPER_ADMIN
  );

  const options = selectableGroups.map(group => ({
    value: group.id.toString(),
    label: group.isDefault ? formatDefaultLabel(group.name) : group.name,
  }));

  const defaultGroup = selectableGroups.find(group => group.isDefault);

  return {
    options,
    defaultGroupValue: defaultGroup?.id.toString(),
  };
};
